import React, { useCallback, useRef, useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import { forEach, isNumber, map, transform } from 'lodash';
import { getTranslatedKPINameV2, translate } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';
import { afterChartReDrawStringData, chartLegendItemClick, setColor, calculateDynamicChartHeight } from '../../../helpers';
import { renderToStaticMarkup } from 'react-dom/server';
import { TrendTooltip } from 'sharedComponents/ChartComponents/Trend';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import { defaultBoostThreshold } from 'helpers/constants';
import { getMeasurementUnitSymbol } from 'helpers/unitHelper';
import {
    Alignment,
    CHART_CONDITION_TREND_KPI_TYPE,
    CLOSE_LABEL_PIXEL_THRESHOLD,
    coolingAnomalyLevel,
    LABEL_EDGE_MARGIN,
    MIN_LABEL_MAX_WIDTH,
    PLOTLINE_DEFAULT_LABEL_Y,
    PLOTLINE_STACKED_LABEL_Y,
    statusBarColors,
    PRINT_FONT_FAMILY,
    RETRAINING_START_PLOTLINE_ID,
    RETRAINING_TRIGGERED_PLOTLINE_ID,
    RETRAINING_COMPLETED_PLOTLINE_ID
} from './constants';
import { RETRAINING_STATUS } from '../../MotorAnomalyDetection/constants';
import { chartHeightConstants } from '../../../constants';
import CoolingAnomalyTooltip from './CoolingAnomalyTooltip';
import { getXAxisLabelDateFormatForDriveAnomaly } from '../helpers';

const STATUS_BAR_LAYOUT = {
    // Keep cooling anomaly pane proportions aligned with the previous visual layout.
    signalAreaHeightPercent: 82,
    statusBarTopPercent: 91.5,
    statusBarHeightPercent: 8,
    minTitleGapPx: 8,
    maxTitleGapPx: 24,
    titleX: 12,
};

const renderOverallStatusLabel = (chart) => {
    const labelText = translate('ABB.Powertrain.Frontend.anomalyOverallStatusBarLabel');
    const signalBottomY = chart.plotTop + STATUS_BAR_LAYOUT.signalAreaHeightPercent / 100 * chart.plotHeight;
    const statusTopY = chart.plotTop + STATUS_BAR_LAYOUT.statusBarTopPercent / 100 * chart.plotHeight;
    const safeTopY = signalBottomY + STATUS_BAR_LAYOUT.minTitleGapPx;
    const safeBottomY = statusTopY - STATUS_BAR_LAYOUT.minTitleGapPx;
    const targetCenterY = safeTopY <= safeBottomY
        ? (safeTopY + safeBottomY) / 2
        : statusTopY - STATUS_BAR_LAYOUT.minTitleGapPx;
    const targetX = chart.plotLeft + STATUS_BAR_LAYOUT.titleX;

    if (!chart._overallStatusLabel) {
        chart._overallStatusLabel = chart.renderer
            .text(labelText, targetX, targetCenterY)
            .css({
                fontFamily: PRINT_FONT_FAMILY,
                fontWeight: 400,
                fontSize: '12px',
                color: colors.tundora,
            })
            .attr({ zIndex: 7 })
            .add();
    }

    chart._overallStatusLabel.attr({ text: labelText, x: targetX });
    const bbox = chart._overallStatusLabel.getBBox();
    chart._overallStatusLabel.attr({ y: targetCenterY + bbox.height * 0.35 });
};

const updateOppositeAxis = (axis, idx) => {
    axis.update({ opposite: idx % 2 !== 0 }, false);
};

const getVisibleSignalAxes = (chart) =>
    chart?.yAxis?.filter(axis => axis?.options?.isSignalYAxis && axis?.visible);

const collectSeriesVisibility = (series, acc) => {
    if (series?.options?.showInLegend !== false && series?.options?.id) {
        acc[series?.options?.id] = series?.visible;
    }
};

const collectYAxisVisibility = (axis, visibilityAcc, oppositeAcc) => {
    if (axis?.options?.id) {
        visibilityAcc[axis?.options?.id] = axis?.visible;
        oppositeAcc[axis?.options?.id] = axis?.opposite;
    }
};

// Check if any series belonging to the given yAxis is still visible
const anySeriesInAxisVisible = (chart, yAxisId, yAxis) =>
    chart?.series?.some(s =>
        (s?.options?.yAxis === yAxisId || s?.yAxis === yAxis) &&
        s?.visible &&
        s?.options?.showInLegend !== false
    );

// Merge a config-axis with live visibility/opposite state
const syncAxisFromLiveChart = (liveChart, axis) => {
    const liveAxis = liveChart?.get(axis?.id);
    if (!liveAxis) { return axis; }
    return { ...axis, visible: liveAxis?.visible, opposite: liveAxis?.opposite };
};

// Merge a config-series with live visibility state
const syncSeriesFromLiveChart = (liveChart, s) => {
    const liveSeries = liveChart?.series?.find(cs => cs?.options?.id === s?.id);
    if (!liveSeries) { return s; }
    return { ...s, visible: liveSeries?.visible };
};

// Applies visibility sync, state capture and redraw after a legend click tick
const applyLegendClickAfterTick = (chart, yAxis, yAxisId, chartConfig, setState) => {
    const anySeriesVisible = anySeriesInAxisVisible(chart, yAxisId, yAxis);
    yAxis.update({ visible: anySeriesVisible }, false);
    const visibleYAxis = getVisibleSignalAxes(chart);
    visibleYAxis.forEach(updateOppositeAxis);
    chartConfig.yAxis = chartConfig.yAxis.map(axis => syncAxisFromLiveChart(chart, axis));
    chartConfig.series = chartConfig.series.map(s => syncSeriesFromLiveChart(chart, s));
    const currentSeriesVisibility = {};
    const currentYAxisVisibility = {};
    const currentYAxisOpposite = {};
    chart.series.forEach(series => collectSeriesVisibility(series, currentSeriesVisibility));
    chart.yAxis.forEach(axis => collectYAxisVisibility(axis, currentYAxisVisibility, currentYAxisOpposite));
    setState.setSeriesVisibility(currentSeriesVisibility);
    setState.setYAxisVisibility(currentYAxisVisibility);
    setState.setYAxisOpposite(currentYAxisOpposite);
    chart.redraw();
    setState.setOptions({ ...chartConfig });
};

const MAX_VISIBLE_SIGNALS = 5;

const isValidNumber = (value) =>
    isNumber(value) && !Number.isNaN(value) && Number.isFinite(value);

const getAnomalyTrendData = (timestamp, anomalyDetectionKPI, conditionTrendsData) => {
    const trendPoint = anomalyDetectionKPI?.trend?.find(
        point => new Date(point.timestamp).getTime() === timestamp
    );
    const kpiTrendData = conditionTrendsData?.find(item => item.type === CHART_CONDITION_TREND_KPI_TYPE);
    const kpiPoint = kpiTrendData?.values?.find(
        value => new Date(value.timestamp).getTime() === timestamp
    );

    let faults = 0;
    let warnings = 0;
    if (kpiPoint?.events && Array.isArray(kpiPoint.events)) {
        kpiPoint.events.forEach(event => {
            if (event.severity === 'Fault' || event.severity === 'Error') { faults++; }
            else if (event.severity === 'Warning') { warnings++; }
        });
    }
    return { mean: trendPoint?.value, min: trendPoint?.min, max: trendPoint?.max, faults, warnings };
};


const getPrintableTitle = (assetDetails, chartTitle) =>
    renderToStaticMarkup(
        <span>{`${assetDetails?.details?.assetName} - ${chartTitle}`}</span>
    );

const getDriveChartConfig = (conditionTrendsData, removeKPIData, title, assetDetails, anomalyDetectionKPI) => {
    return {
        title: { text: null },
        chart: {
            events: {
                render: function (chartInstance) {
                    renderOverallStatusLabel(this);
                    // Skip expensive render operations during print
                    if (this._renderEventDisabled) {
                        return;
                    }
                    afterChartReDrawStringData(chartInstance.target.container.id, removeKPIData);
                },
                beforePrint: function () {
                    const printableTitle = getPrintableTitle(assetDetails, title);
                    if (this.exportSVGElements?.length > 0) {
                        this.exportSVGElements[0]?.hide();
                    }

                    // Temporarily disable render event during print
                    this._renderEventDisabled = true;

                    this.setTitle({
                        text: printableTitle,
                        align: 'left',
                        x: 50,
                        y: -10,
                    });

                    // Use direct CSS updates instead of this.update() to avoid full re-render
                    const legendElement = this.legend?.group?.element;
                    if (this.xAxis?.length > 0) {
                        const xAxisLabels = this.xAxis[0]?.labelGroup?.element;
                        if (xAxisLabels?.style) {
                            xAxisLabels.style.fontFamily = PRINT_FONT_FAMILY;
                        }
                    }

                    this.yAxis?.forEach(axis => {
                        const yAxisLabels = axis.labelGroup?.element;
                        if (yAxisLabels?.style) {
                            yAxisLabels.style.fontFamily = PRINT_FONT_FAMILY;
                        }
                    });

                    if (legendElement?.style) {
                        legendElement.style.fontFamily = PRINT_FONT_FAMILY;
                    }
                },
                afterPrint: function () {
                    if (this.exportSVGElements?.length > 0) {
                        this.exportSVGElements[0]?.show();
                    }
                    this.setTitle({
                        text: '',
                    });
                    this._renderEventDisabled = false;
                    this.redraw();
                }
            },
            style: {
                fontFamily: PRINT_FONT_FAMILY
            },
            spacingTop: 70,
            spacingRight: 25,
        },
        boost: {
            allowForce: false //Workaround for highchart bug. Allows correctly disabling boost, when zooming in.
        },
        time: { useUTC: false },
        enableNoDataZoom: true,
        navigator: { enabled: true },
        xAxis: {
            type: 'datetime',
            id: 'x',
            dateTimeLabelFormats: {
                millisecond: '%H:%M:%S',
                day: '%e. %b',
                month: '%b \'%y'
            },
            crosshair: { enabled: true, width: 2, color: colors.tundora },
            labels: {
                rotation: 50,
                color: '#000000',
                style: { fontWeight: 'bold', fontColor: '#000000' },
                formatter: function () {
                    return getXAxisLabelDateFormatForDriveAnomaly(this.value);
                }
            },
            lineColor: '#000000',
            lineWidth: 2,
            tickPixelInterval: 25,
            tickColor: '#000000',
            tickWidth: 2,
        },
        legend: {
            useHTML: true,
            layout: 'horizontal',
            align: 'right',
            itemStyle: { fontFamily: PRINT_FONT_FAMILY, fontSize: '14px' },
            itemDistance: 16,
            itemMarginTop: 10,
            symbolRadius: 0,
            squareSymbol: false,
            symbolHeight: 8,
            symbolWidth: 8,
        },
        credits: { enabled: false },
        tooltip: {
            shared: true,
            valueDecimals: 2,
            useHTML: true,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            shadow: false,
            padding: 0,
            borderWidth: 0,
            dateTimeLabelFormats: {
                day: '%d.%m.%Y<br/>%H:%M',
                hour: '%d.%m %H:%M',
            },
            positioner: function (labelWidth, labelHeight, point) {
                const chart = this.chart;
                let x = point.plotX + chart.plotLeft;
                let y = point.plotY + chart.plotTop - labelHeight - 16;
                x = Math.min(x, chart.plotLeft + chart.plotWidth - labelWidth);
                y = Math.max(chart.plotTop, y);
                return { x, y };
            },
            formatter: function () {
                const coolingAnomalySeriesVisible = this.points?.some(
                    p => p?.series?.options?.id === 'cooling_anomaly_level'
                );
                const conditionTrendsSignalsVisible = this.points?.some(
                    p => p?.series?.options?.id?.startsWith('Anomaly_') &&
                        p?.series?.options?.id !== 'cooling_anomaly_level'
                );

                if (coolingAnomalySeriesVisible && !conditionTrendsSignalsVisible && anomalyDetectionKPI) {
                    const anomalyData = getAnomalyTrendData(this.x, anomalyDetectionKPI, conditionTrendsData);
                    return renderToStaticMarkup(
                        <CoolingAnomalyTooltip
                            timeStamp={this.x}
                            mean={anomalyData?.mean}
                            min={anomalyData?.min}
                            max={anomalyData?.max}
                            faultCount={anomalyData?.faults}
                            warningCount={anomalyData?.warnings}
                            title={translate('ABB.Powertrain.Frontend.coolingAnomalyChartLegend')}
                        />
                    );
                }

                return renderToStaticMarkup(
                    <TrendTooltip
                        points={this.points?.filter(
                            point => ![CHART_CONDITION_TREND_KPI_TYPE].includes(point?.series?.name)
                        )}
                        timeStamp={this.x}
                        isUTC={false}
                        anomalyLevel={null}
                    />
                );
            },
        },
        plotOptions: {
            series: {
                turboThreshold: 0,
                animation: false,
                boostThreshold: 1000,
            },
            scatter: { marker: { enabled: true }, lineWidth: 0 },
            column: {
                boostThreshold: 1000,
                groupPadding: 0,
                pointPadding: 0,
                borderWidth: 0,
                pointPlacement: 0.5,
            }
        },
        navigation: {
            buttonOptions: { align: 'right' },
            menuStyle: {
                borderColor: '#fff',
                boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
                borderRadius: '6px'
            },
            menuItemStyle: { padding: '6px 12px', fontSize: '14px', fontFamily: PRINT_FONT_FAMILY },
            menuItemHoverStyle: { backgroundColor: '#ebebeb', color: '#1f1f1f' }
        },
        lang: {
            printChart: translate('ABB.Powertrain.Frontend.printChart'),
            downloadPNG: translate('ABB.Powertrain.Frontend.saveAsImage'),
            noData: renderToStaticMarkup(
                <ChartNoDataText
                    title={translate('ABB.Powertrain.Frontend.chartNoMeasurementData')}
                    text={translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide')}
                />
            )
        },
        noData: { useHTML: true },
        exporting: {
            enabled: true,
            scale: 1.5,
            sourceWidth: 1100,
            sourceHeight: 500,
            fallbackToExportServer: false,
            allowHTML: true,
            printMaxWidth: 1100,
            buttons: {
                contextButton: {
                    enabled: false,
                    menuItems: ['downloadPNG', 'printChart'],
                    verticalAlign: 'top',
                    x: 0,
                    y: -70
                }
            },
            chartOptions: {
                legend: {
                    useHTML: false,
                    squareSymbol: true,
                    symbolRadius: 0,
                    symbolHeight: 10,
                    symbolWidth: 10,
                },
                title: {
                    text: `${assetDetails?.details?.assetName} - ${title}`,
                    align: 'left',
                    x: 50,
                    y: -10,
                    style: {
                        fontFamily: 'Verdana, sans-serif',
                        fontWeight: 'bold'
                    }
                },
                xAxis: { labels: { style: { fontFamily: 'Verdana, sans-serif' } } },
                yAxis: { labels: { style: { fontFamily: 'Verdana, sans-serif' } } }
            }
        }
    };
};


const getStatusBarData = (trendValues) => {
    return map(trendValues?.values, ul => ({
        y: 1,
        x: new Date(ul?.timestamp)?.getTime(),
        color: statusBarColors[ul?.value],
    }));
};


const getDriveSeries = (conditionTrendsData, valueType, chartInstance, anomalyDetectionKPI) => {
    const seriesConfig = { yAxis: [], seriesData: [], allSeriesLength: 0 };
    const coloredTrends = setColor(conditionTrendsData);

    // Cooling anomaly trend series (the main line)
    if (anomalyDetectionKPI?.trend?.length > 0) {
        const trendYAxisId = 'y_cooling_anomaly_level';
        const trendSeriesId = 'cooling_anomaly_level';
        const trendName = getTranslatedKPINameV2(anomalyDetectionKPI) ||
            translate('ABB.Powertrain.Frontend.coolingAnomalyChartLegend');

        const trendDataPoints = anomalyDetectionKPI.trend.map(point => ({
            x: new Date(point.timestamp).getTime(),
            y: point.value,
        }));

        const trendRangeData = anomalyDetectionKPI.trend
            .filter(point => {
                const hasValidMin = isValidNumber(point.min);
                const hasValidMax = isValidNumber(point.max);
                return hasValidMin && hasValidMax && point.timestamp !== null && point.min <= point.max;
            })
            .map(point => [new Date(point.timestamp).getTime(), point.min, point.max]);

        seriesConfig.yAxis.push({
            top: '0%',
            height: `${STATUS_BAR_LAYOUT.signalAreaHeightPercent}%`,
            labels: { format: '{value}', style: { color: colors.cerulean } },
            id: trendYAxisId,
            min: 0,
            title: {
                text: translate('ABB.Powertrain.Frontend.coolingAnomalyChartLegend'),
                enabled: true,
                style: { color: colors.cerulean }
            },
            isSignalYAxis: true
        });

        if (trendRangeData.length > 0) {
            seriesConfig.seriesData.push({
                name: `${trendName} Range`,
                data: trendRangeData,
                type: 'arearange',
                id: 'rangeSeries',
                lineWidth: 1,
                linkedTo: trendSeriesId,
                fillOpacity: 0.5,
                opacity: 1,
                color: colors.silver,
                lineColor: colors.silverTransparent,
                zIndex: 0,
                yAxis: trendYAxisId,
                xAxis: 'x',
                showInLegend: false,
                visible: true,
                enableMouseTracking: false,
                tooltip: {
                    valueSuffix: '',
                    valueDecimals: 2,
                    pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.low:.2f} - {point.high:.2f}</b><br/>'
                },
                marker: { enabled: false },
                states: { hover: { enabled: false } }
            });
        }

        seriesConfig.seriesData.push({
            name: trendName,
            data: trendDataPoints,
            tooltip: { valueDecimals: 1 },
            color: colors.coolingAnomaly,
            showInNavigator: true,
            visible: true,
            showInLegend: true,
            marker: { enabled: true, radius: 2, symbol: 'square', lineWidth: 0 },
            lineWidth: 1,
            id: trendSeriesId,
            yAxis: trendYAxisId,
            xAxis: 'x',
            enableMouseTracking: true
        });
    }

    // Condition trend signals and status bar
    if (coloredTrends?.length) {
        transform(coloredTrends, (acc, item) => {
            if (item.type === CHART_CONDITION_TREND_KPI_TYPE) {
                const anomalyStatusBarYAxisId = 'anomaly_status_bar_yaxis';

                acc.yAxis.push({
                    top: `${STATUS_BAR_LAYOUT.statusBarTopPercent}%`,
                    height: `${STATUS_BAR_LAYOUT.statusBarHeightPercent}%`,
                    id: anomalyStatusBarYAxisId,
                    title: {
                        text: null,
                        enabled: false,
                    },
                    labels: { enabled: false },
                    gridLineWidth: 0,
                    lineWidth: 0,
                    tickLength: 0,
                });
                acc.seriesData.push({
                    name: CHART_CONDITION_TREND_KPI_TYPE,
                    type: 'column',
                    id: `${crypto.randomUUID()}`,
                    yAxis: anomalyStatusBarYAxisId,
                    xAxis: 'x',
                    data: getStatusBarData(item),
                    showInLegend: false,
                    enableMouseTracking: false,
                    states: { inactive: { enabled: false }, hover: { enabled: false } }
                });
            } else {
                const unit = item.unit || '';
                const convertedUnit = getMeasurementUnitSymbol(item.unitGroupId);
                const precision = item.precision;
                const yAxisId = `y_${item.key}`;
                const seriesId = item.key;
                const name = item?.timeseriesName;

                const trendDataValues = [];
                forEach(item.values, measurement => {
                    if (measurement.timestamp !== null) {
                        trendDataValues.push([new Date(measurement.timestamp).getTime(), measurement.value]);
                    }
                });

                const unitLabel = convertedUnit || unit;
                const yAxisTitleText = unitLabel ? `${name} (${unitLabel})` : name;
                acc.yAxis.push({
                    top: '0%',
                    height: `${STATUS_BAR_LAYOUT.signalAreaHeightPercent}%`,
                    labels: { format: '{value}', style: { color: item.color } },
                    id: yAxisId,
                    title: {
                        text: yAxisTitleText,
                        enabled: true,
                        style: { color: item.color }
                    },
                    // All condition trend signals start hidden — user can toggle via legend
                    visible: false,
                    isSignalYAxis: true
                });
                const valueTypeSuffix = valueType ? ` (${valueType})` : '';
                acc.seriesData.push({
                    name: `${name}${valueTypeSuffix}`,
                    data: trendDataValues,
                    tooltip: {
                        valueSuffix: ` ${convertedUnit || unit}`,
                        valueDecimals: precision,
                    },
                    color: item.color,
                    showInNavigator: true,
                    visible: false,
                    showInLegend: true,
                    marker: { enabled: true, radius: 2, symbol: 'square', lineWidth: 0 },
                    lineWidth: 1,
                    id: seriesId,
                    yAxis: yAxisId,
                    xAxis: 'x',
                    enableMouseTracking: true
                });
            }
        }, seriesConfig);
    }

    const allSeriesLength = seriesConfig.seriesData?.length
        ? seriesConfig.seriesData.reduce((count, series) => series.data?.length + count, 0)
        : 0;

    return { ...seriesConfig, allSeriesLength };
};

//Preserve the exact timestamp to keep retraining plotlines aligned.
const dateToEpoch = (date) => {
    if (!date) { return null; }
    const epoch = new Date(date).getTime();
    return Number.isNaN(epoch) ? null : epoch;
};

const getPlotBounds = (chartInstance) => {
    const plotLeft = chartInstance?.plotLeft ?? 0;
    const plotWidth = chartInstance?.plotWidth ?? 0;
    return { plotLeft, plotWidth, plotRight: plotLeft + plotWidth };
};

const getPixelPosition = (dateEpoch, chartInstance) => {
    const { min, max } = chartInstance?.xAxis?.[0] || {};
    const { plotLeft, plotWidth } = getPlotBounds(chartInstance);
    if (min === null || min === undefined || max === null || max === undefined || max <= min || !plotWidth) {
        return null;
    }
    const position = (dateEpoch - min) / (max - min);
    return plotLeft + position * plotWidth;
};

const isPresent = (value) => value !== null && value !== undefined;

const resolveLabelSide = (side, linePixel, plotLeft, plotWidth) => {
    if (isPresent(side)) { return side; }
    const isNearRightEdge = isPresent(linePixel) && plotWidth > 0 && (linePixel - plotLeft) / plotWidth > 0.7;
    return isNearRightEdge ? Alignment.LEFT : Alignment.RIGHT;
};

const getLeftBoundary = (plotLeft, neighborPixel, linePixel) => {
    if (isPresent(neighborPixel) && neighborPixel <= linePixel) {
        return Math.max(plotLeft, neighborPixel);
    }
    return plotLeft;
};

const getAvailableSpace = (linePixel, side, leftBoundary, plotRight) => {
    if (isPresent(linePixel)) {
        return side === Alignment.LEFT
            ? linePixel - leftBoundary - LABEL_EDGE_MARGIN
            : plotRight - linePixel - LABEL_EDGE_MARGIN;
    }
    return null;
};

const getLabelSide = (dateEpoch, chartInstance, forceSide, neighborEpoch) => {
    const { plotLeft, plotWidth, plotRight } = getPlotBounds(chartInstance);
    const linePixel = getPixelPosition(dateEpoch, chartInstance);
    const neighborPixel = isPresent(neighborEpoch) ? getPixelPosition(neighborEpoch, chartInstance) : null;

    let side = resolveLabelSide(forceSide, linePixel, plotLeft, plotWidth);
    const leftBoundary = getLeftBoundary(plotLeft, neighborPixel, linePixel);
    const leftSpace = getAvailableSpace(linePixel, Alignment.LEFT, leftBoundary, plotRight);
    const rightSpace = getAvailableSpace(linePixel, Alignment.RIGHT, leftBoundary, plotRight);
    let availableSpace = side === Alignment.LEFT ? leftSpace : rightSpace;

    // If there is no room on the right, always move label to the left to avoid right-edge clipping.
    if (side === Alignment.RIGHT && isPresent(rightSpace) && rightSpace < MIN_LABEL_MAX_WIDTH) {
        side = Alignment.LEFT;
        availableSpace = leftSpace;
    } else if (side === Alignment.LEFT && isPresent(leftSpace) && leftSpace < MIN_LABEL_MAX_WIDTH) {
        if (isPresent(rightSpace) && rightSpace > leftSpace) {
            side = Alignment.RIGHT;
            availableSpace = rightSpace;
        }
    }

    const maxWidth = isPresent(availableSpace) ? Math.max(availableSpace, 0) : null;

    if (side === Alignment.LEFT) {
        return { labelAlign: Alignment.RIGHT, labelX: -8, maxWidth };
    }
    return { labelAlign: Alignment.LEFT, labelX: 8, maxWidth };
};

const pickSideWithMoreRoom = (dateEpoch, neighborEpoch, chartInstance) => {
    const { plotLeft, plotRight } = getPlotBounds(chartInstance);
    const linePixel = getPixelPosition(dateEpoch, chartInstance);
    const neighborPixel = getPixelPosition(neighborEpoch, chartInstance);

    if (linePixel === null) { return Alignment.RIGHT; }

    const rightSpace = plotRight - linePixel;
    const leftBoundary = neighborPixel === null ? plotLeft : Math.max(plotLeft, neighborPixel);
    const leftSpace = linePixel - leftBoundary;

    return leftSpace > rightSpace ? Alignment.LEFT : Alignment.RIGHT;
};

const buildPlotline = (dateEpoch, id, labelKey, chartInstance, forceSide, neighborEpoch, labelY = PLOTLINE_DEFAULT_LABEL_Y) => {
    const { labelAlign, labelX, maxWidth } = getLabelSide(dateEpoch, chartInstance, forceSide, neighborEpoch);
    const labelText = translate(`ABB.Powertrain.Frontend.${labelKey}`);
    return {
        id,
        color: 'rgba(0, 0, 0, 0.24)',
        width: 3,
        value: dateEpoch,
        zIndex: 5,
        label: {
            y: labelY,
            x: labelX,
            align: labelAlign,
            rotation: 0,
            useHTML: false,
            style: {
                fontFamily: PRINT_FONT_FAMILY,
                color: colors.nero,
                fontSize: '12px',
                fontWeight: '500',
                fontStyle: 'normal',
                lineHeight: '14px',
                textOverflow: 'clip',
                ...maxWidth ? { width: `${Math.round(maxWidth)}px` } : {}
            },
            text: labelText
        }
    };
};

const getStartAndTriggeredSideConfig = (startEpoch, triggeredEpoch, chartInstance) => {
    const initialConfig = {
        startForceSide: null,
        triggeredForceSide: null,
        startNeighborEpoch: null,
        triggeredNeighborEpoch: null,
    };

    if (isPresent(startEpoch) && isPresent(triggeredEpoch)) {
        const startIsEarlier = startEpoch <= triggeredEpoch;
        const earlierEpoch = startIsEarlier ? startEpoch : triggeredEpoch;
        const laterEpoch = startIsEarlier ? triggeredEpoch : startEpoch;
        const earlierForceSide = Alignment.LEFT;
        const laterForceSide = pickSideWithMoreRoom(laterEpoch, earlierEpoch, chartInstance);

        if (startIsEarlier) {
            return {
                startForceSide: earlierForceSide,
                triggeredForceSide: laterForceSide,
                startNeighborEpoch: null,
                triggeredNeighborEpoch: startEpoch,
            };
        }

        return {
            startForceSide: laterForceSide,
            triggeredForceSide: earlierForceSide,
            startNeighborEpoch: triggeredEpoch,
            triggeredNeighborEpoch: null,
        };
    }

    return initialConfig;
};

const getCompletedSideConfig = (startEpoch, triggeredEpoch, completedEpoch, chartInstance) => {
    if (!isPresent(completedEpoch)) {
        return { completedNeighborEpoch: null, completedForceSide: null };
    }

    const candidateNeighborEpochs = [startEpoch, triggeredEpoch]
        .filter(epoch => isPresent(epoch) && epoch <= completedEpoch)
        .sort((left, right) => right - left);
    const nearestEarlierEpoch = candidateNeighborEpochs[0];

    if (!isPresent(nearestEarlierEpoch)) {
        return { completedNeighborEpoch: null, completedForceSide: null };
    }

    return {
        completedNeighborEpoch: nearestEarlierEpoch,
        completedForceSide: pickSideWithMoreRoom(completedEpoch, nearestEarlierEpoch, chartInstance),
    };
};

const shouldStackRetrainingLabel = (firstEpoch, secondEpoch, chartInstance) => {
    const firstPixel = isPresent(firstEpoch) ? getPixelPosition(firstEpoch, chartInstance) : null;
    const secondPixel = isPresent(secondEpoch) ? getPixelPosition(secondEpoch, chartInstance) : null;
    if (!isPresent(firstPixel) || !isPresent(secondPixel)) { return false; }
    const overlapThreshold = Math.max(CLOSE_LABEL_PIXEL_THRESHOLD, MIN_LABEL_MAX_WIDTH * 2 + LABEL_EDGE_MARGIN);
    return Math.abs(secondPixel - firstPixel) < overlapThreshold;
};

const createRetrainingPlotlines = (retrainingStartDate, requestedSubmittedDate, retrainingCompletedDate, retrainingStatus, chartInstance) => {
    const startEpoch = dateToEpoch(retrainingStartDate);
    const triggeredEpoch = dateToEpoch(requestedSubmittedDate);
    const isRetrainingCompleted = retrainingStatus === RETRAINING_STATUS.COMPLETED;
    const completedEpoch = isRetrainingCompleted ? dateToEpoch(retrainingCompletedDate) : null;

    const {
        startForceSide,
        triggeredForceSide,
        startNeighborEpoch,
        triggeredNeighborEpoch,
    } = getStartAndTriggeredSideConfig(startEpoch, triggeredEpoch, chartInstance);

    const {
        completedNeighborEpoch,
        completedForceSide,
    } = getCompletedSideConfig(startEpoch, triggeredEpoch, completedEpoch, chartInstance);

    const shouldStackTriggeredLabel = shouldStackRetrainingLabel(startEpoch, triggeredEpoch, chartInstance);
    const shouldStackCompletedLabel = [startEpoch, triggeredEpoch]
        .filter(isPresent)
        .some(epoch => shouldStackRetrainingLabel(epoch, completedEpoch, chartInstance));

    const plotlines = [];
    if (isPresent(startEpoch)) {
        plotlines.push(buildPlotline(startEpoch, RETRAINING_START_PLOTLINE_ID, 'retrainingStartingDateLabel', chartInstance, startForceSide, startNeighborEpoch));
    }
    if (isPresent(triggeredEpoch)) {
        plotlines.push(buildPlotline(
            triggeredEpoch,
            RETRAINING_TRIGGERED_PLOTLINE_ID,
            'retrainingTriggered',
            chartInstance,
            triggeredForceSide,
            triggeredNeighborEpoch,
            shouldStackTriggeredLabel ? PLOTLINE_STACKED_LABEL_Y : PLOTLINE_DEFAULT_LABEL_Y
        ));
    }
    if (isPresent(completedEpoch)) {
        plotlines.push(buildPlotline(
            completedEpoch,
            RETRAINING_COMPLETED_PLOTLINE_ID,
            'retrainingCompletedPlotline',
            chartInstance,
            completedForceSide,
            completedNeighborEpoch,
            shouldStackCompletedLabel ? PLOTLINE_STACKED_LABEL_Y : PLOTLINE_DEFAULT_LABEL_Y
        ));
    }
    return plotlines;
};

const useDriveAnomalyChartConfig = (
    anomalyDetectionKPI,
    removeKPIData,
    title,
    assetDetails,
    valueType,
    conditionTrendsData,
    { containerRef, chartInstance, chartHeight, retrainingStartDate, requestedSubmittedDate, retrainingCompletedDate, retrainingStatus }
) => {
    const [options, setOptions] = useState({});
    const [navigatorExtremes, setNavigatorExtremes] = useState(null);
    const [seriesVisibility, setSeriesVisibility] = useState({});
    const [yAxisVisibility, setYAxisVisibility] = useState({});
    const [yAxisOpposite, setYAxisOpposite] = useState({});
    const visibleSignalsQueueRef = useRef([]);

    const chartConfig = getDriveChartConfig(
        conditionTrendsData,
        removeKPIData,
        title,
        assetDetails,
        anomalyDetectionKPI
    );

    const handleSetExtremes = useCallback((e) => {
        if (e.trigger === 'navigator') {
            setNavigatorExtremes({ min: e.min, max: e.max, trigger: e.trigger });
        }
    }, [setNavigatorExtremes]);

    // Track the latest chartInstance in a stable ref so popout open/close (which swaps chartInstance) doesn't re-trigger the rebuild effect and reset the selected KPI.
    const chartInstanceRef = useRef(null);
    useEffect(() => {
        chartInstanceRef.current = chartInstance;
    }, [chartInstance]);

    const captureCurrentStates = useCallback(() => {
        const instance = chartInstanceRef.current;
        if (instance) {
            const currentSeriesVisibility = {};
            const currentYAxisVisibility = {};
            const currentYAxisOpposite = {};
            instance?.series?.forEach(series => {
                if (series.options.showInLegend !== false && series.options.id) {
                    currentSeriesVisibility[series.options.id] = series.visible;
                }
            });
            instance?.yAxis?.forEach(axis => {
                if (axis.options.id) {
                    currentYAxisVisibility[axis.options.id] = axis.visible;
                    currentYAxisOpposite[axis.options.id] = axis.opposite;
                }
            });
            setSeriesVisibility(currentSeriesVisibility);
            setYAxisVisibility(currentYAxisVisibility);
            setYAxisOpposite(currentYAxisOpposite);
        }
    }, []);

    useEffect(() => {
        const containerHeight = containerRef?.current?.clientHeight;
        chartConfig.chart.height = calculateDynamicChartHeight(
            conditionTrendsData,
            true, // isDriveAnomaly = true
            containerHeight || chartHeightConstants.default
        );
    }, []);

    useEffect(() => {
        if (chartInstance?.xAxis?.length > 0) {
            chartInstance.xAxis[0]?.update({
                ...navigatorExtremes && { min: navigatorExtremes?.min, max: navigatorExtremes?.max }
            });
        }
    }, [navigatorExtremes, chartInstance]);

    // Retraining plotline management
    useEffect(() => {
        if (!chartInstance?.xAxis?.length) { return; }
        const xAxis = chartInstance.xAxis[0];
        if (!xAxis) { return; }
        let lastGeometrySignature = null;
        const refreshRetrainingPlotlines = () => {
            const { min, max } = xAxis || {};
            const geometrySignature = `${chartInstance.plotLeft}|${chartInstance.plotWidth}|${min}|${max}`;
            if (geometrySignature === lastGeometrySignature) { return; }
            lastGeometrySignature = geometrySignature;

            xAxis.removePlotLine(RETRAINING_START_PLOTLINE_ID);
            xAxis.removePlotLine(RETRAINING_TRIGGERED_PLOTLINE_ID);
            xAxis.removePlotLine(RETRAINING_COMPLETED_PLOTLINE_ID);
            const plotlines = createRetrainingPlotlines(retrainingStartDate, requestedSubmittedDate, retrainingCompletedDate, retrainingStatus, chartInstance);
            plotlines.forEach(plotline => xAxis.addPlotLine(plotline));
        };

        refreshRetrainingPlotlines();
        const removeRedrawListener = Highcharts.addEvent(chartInstance, 'redraw', refreshRetrainingPlotlines);

        return () => {
            removeRedrawListener();
        };
    }, [chartInstance, retrainingStartDate, requestedSubmittedDate, retrainingCompletedDate, retrainingStatus]);

    const autoHideOldestSignalIfNeeded = (chart) => {
        while (visibleSignalsQueueRef.current.length > MAX_VISIBLE_SIGNALS) {
            const oldestVisibleSignalId = visibleSignalsQueueRef.current[0];
            visibleSignalsQueueRef.current = visibleSignalsQueueRef.current.slice(1);

            const oldestSeries = chart?.series?.find(
                series => series?.options?.id === oldestVisibleSignalId && series?.visible
            );
            if (!oldestSeries) { continue; }
            oldestSeries?.setVisible(false, false);
            const yAxisToHide = chart?.get(oldestSeries.options.yAxis) || oldestSeries.yAxis;
            if (yAxisToHide) {
                yAxisToHide.update({ visible: false }, false);
            }
        }
    };

    useEffect(() => {
        captureCurrentStates();

        const { yAxis, seriesData, allSeriesLength } = getDriveSeries(
            conditionTrendsData,
            valueType,
            chartInstance,
            anomalyDetectionKPI
        );

        const updatedSeriesData = seriesData.map(series => {
            if (seriesVisibility[series.id] !== undefined) {
                return { ...series, visible: seriesVisibility[series.id] };
            }
            return series;
        });

        const updatedYAxis = yAxis.map(axis => {
            const updates = { ...axis };
            if (yAxisVisibility[axis.id] !== undefined) { updates.visible = yAxisVisibility[axis.id]; }
            if (yAxisOpposite[axis.id] !== undefined) { updates.opposite = yAxisOpposite[axis.id]; }
            return updates;
        });

        chartConfig.xAxis = {
            ...chartConfig.xAxis,
            events: { afterSetExtremes: handleSetExtremes }
        };
        chartConfig.yAxis = updatedYAxis;
        chartConfig.series = updatedSeriesData;
        chartConfig.plotOptions.series.boostThreshold =
            allSeriesLength > defaultBoostThreshold ? 1 : 0;

        // Legend item click — maintain max 6 visible signals, cooling anomaly always stays visible
        chartConfig.plotOptions.series.events = {
            legendItemClick: function (e) {
                const chart = this.chart;

                // Cooling anomaly score is always visible — block hiding it
                if (this.options.id === coolingAnomalyLevel && this.visible) {
                    return false;
                }

                // When enabling a signal, enforce the MAX_VISIBLE_SIGNALS limit
                if (!this.visible && this.options?.id !== coolingAnomalyLevel) {
                    const clickedId = this.options?.id;

                    // Add to queue if not already present
                    if (!visibleSignalsQueueRef?.current.includes(clickedId)) {
                        visibleSignalsQueueRef.current = [...visibleSignalsQueueRef.current, clickedId];
                    }
                    // If queue exceeds max, hide the oldest enabled signal in FIFO order
                    autoHideOldestSignalIfNeeded(chart);
                } else if (this.visible && this.options?.id !== coolingAnomalyLevel) {
                    // Remove from queue when user manually hides a signal
                    const clickedId = this.options?.id;
                    visibleSignalsQueueRef.current = visibleSignalsQueueRef?.current?.filter(id => id !== clickedId);
                }

                chartLegendItemClick(e);
                const yAxisId = this.options.yAxis;
                const yAxis = chart?.get(yAxisId) || this.yAxis;

                setTimeout(() => {
                    if (yAxis) {
                        applyLegendClickAfterTick(chart, yAxis, yAxisId, chartConfig, {
                            setSeriesVisibility, setYAxisVisibility, setYAxisOpposite, setOptions
                        });
                    }
                }, 0);

                return true;
            }
        };

        setOptions({ ...chartConfig });
    }, [
        anomalyDetectionKPI,
        valueType,
        conditionTrendsData,
        captureCurrentStates,
        chartHeight,
    ]);

    return options;
};

export default useDriveAnomalyChartConfig;
